import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
} from '@angular/core';
import {
  IFieldArrayData,
  IFieldData,
  IFieldGroupNameData,
} from '../../models/application-form-data.model';
import { IIremboFileUpload } from '../../models/irembo-file-upload-response.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ToastService } from '../../services/toast/toast.service';
import { IIrembogovBasicLabelKeyPair } from '../../models/irembogov-basic-label-keypair.model';
import {
  fieldCardUtilDownloadFile,
  fieldCardUtilGetImagePath,
  fieldCardUtilGetMultiLineValues,
  fieldCardUtilGetSingleLineValue,
  fieldCardUtilIsImage,
  fieldCardUtilTrackByFn,
  fieldCardUtilGetExternalFileDetails,
  TSingleLineValue,
} from '../component-utils/application-form-field-card-preview.util';
import { TranslateService } from '@ngx-translate/core';
import { KeycloakService } from 'keycloak-angular';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'irembogov-application-form-field-card',
  templateUrl: './application-form-field-card.component.html',
  styleUrls: ['./application-form-field-card.component.scss'],
})
export class ApplicationFormFieldCardComponent implements OnDestroy, OnInit {
  @Input() group: IFieldGroupNameData | undefined;
  @Input() applicationId: string | undefined;
  @Input() baseUrl?: any;
  @Input() applicationPath: string | undefined;

  selectedAttachment: IIremboFileUpload | undefined;
  filePath: SafeResourceUrl | undefined;
  private subscriptions = new Subscription();

  base64Data: string | undefined;
  loadingPreview = false;
  imageObjectUrl: string | undefined;
  currentBlob: Blob | undefined;

  multiLineValueFieldTypes: string[] = ['customdoubleidinput'];
  complexFieldTypes: string[] = [
    'customdatatable',
    'customitemselectiontable',
    'customeditabletable',
    'custommultiselectdatafetch',
    'customtablewithinputform',
  ];

  constructor(
    public readonly modalService: NgbModal,
    private readonly sanitizer: DomSanitizer,
    private readonly http: HttpClient,
    private readonly toast: ToastService,
    private readonly translateService: TranslateService,
    private readonly cd: ChangeDetectorRef,
    private readonly keycloakService: KeycloakService
  ) {}

  ngOnInit(): void {
    this.preloadTranslations();
  }

  async preloadTranslations(): Promise<void> {
    // Call a translate service with a mock word to ensure it's loaded.
    await this.translateService.instant('sample');
  }

  private cleanupObjectUrl(): void {
    if (this.imageObjectUrl) {
      URL.revokeObjectURL(this.imageObjectUrl);
      this.imageObjectUrl = undefined;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  trackByFn(index: number, item: IFieldData | IFieldArrayData) {
    return fieldCardUtilTrackByFn(index);
  }

  getMultiLineValues(
    field: IFieldData | undefined
  ): IIrembogovBasicLabelKeyPair<string | null>[] {
    return fieldCardUtilGetMultiLineValues(
      field,
      this.multiLineValueFieldTypes
    );
  }

  getSingleLineValue(field: IFieldData | undefined): string | null {
    const lineValue: TSingleLineValue = fieldCardUtilGetSingleLineValue(
      field,
      this.multiLineValueFieldTypes
    );

    if (Array.isArray(lineValue)) {
      return lineValue
        .map((item: string) =>
          item && item.trim() ? this.translateService.instant(item) : item
        )
        .join(', ');
    }

    if (lineValue && String(lineValue).trim()) {
      return this.translateService.instant(String(lineValue));
    }

    return lineValue;
  }

  isImage(mimeType: unknown) {
    return fieldCardUtilIsImage(mimeType);
  }

  getAttachmentValue(dataValue: unknown): IIremboFileUpload {
    return dataValue as IIremboFileUpload;
  }

  getExternalAttachmentFileName(dataValue: unknown): string {
    const file = dataValue as IIremboFileUpload;
    return file?.fileName || 'External File';
  }

  getExternalAttachmentDetails(dataValue: unknown) {
    return fieldCardUtilGetExternalFileDetails(dataValue);
  }

  openExternalFileUrl(dataValue: unknown) {
    const details = this.getExternalAttachmentDetails(dataValue);
    if (details.url) {
      window.open(details.url, '_blank');
    }
  }

  previewAttachment(
    content: TemplateRef<unknown>,
    file: unknown,
    index: number
  ) {
    // Clean up any previous blob URL to prevent memory leaks
    this.cleanupObjectUrl();

    this.selectedAttachment = file as IIremboFileUpload;
    this.filePath = undefined;
    this.base64Data = undefined;
    this.currentBlob = undefined;
    this.loadingPreview = true;

    // File content is embedded
    if (!this.selectedAttachment.fileDownloadName) {
      if (this.isImage(this.selectedAttachment.fileType)) {
        this.filePath = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.selectedAttachment.content as string
        );
        // Store base64 for download button to work
        this.base64Data = this.selectedAttachment.content as string;
      } else {
        this.base64Data = this.selectedAttachment.content as string;
      }
      this.loadingPreview = false;
      this.cd.detectChanges();
    }

    // File needs to be fetched from server
    if (this.selectedAttachment.fileDownloadName && this.applicationId) {
      this.subscriptions.add(
        this.getFileFromUrl(
          this.applicationId,
          this.selectedAttachment.fileDownloadName
        ).subscribe({
          next: blob => {
            // Store blob for download functionality
            this.currentBlob = blob;

            if (this.isImage(this.selectedAttachment?.fileType)) {
              // For images: Create blob URL (efficient, native browser support)
              this.imageObjectUrl = URL.createObjectURL(blob);
              this.filePath = this.sanitizer.bypassSecurityTrustResourceUrl(
                this.imageObjectUrl
              );
              this.loadingPreview = false;
              this.cd.detectChanges();
            } else {
              // For PDFs: Convert to base64 for pdf-viewer component
              const reader = new FileReader();
              reader.readAsDataURL(blob);
              reader.onload = () => {
                this.base64Data = reader.result as string;
                this.loadingPreview = false;
                this.cd.detectChanges();
              };
              reader.onerror = () => {
                this.toast.show({
                  body: `Error converting file`,
                  type: 'error',
                });
                this.loadingPreview = false;
                this.cd.detectChanges();
              };
            }
          },
          error: () => {
            this.toast.show({
              body: `Error loading preview`,
              type: 'error',
            });
            this.loadingPreview = false;
            this.cd.detectChanges();
          },
        })
      );
    }

    // Open the modal
    this.modalService.open(content, {
      ariaLabelledBy: 'preview-document',
      size: 'xl',
    });
  }

  getImagePath(file: unknown): string {
    return fieldCardUtilGetImagePath(file);
  }

  getFileFromUrl(id: string, fileDownloadName: string): Observable<Blob> {
    const url = `${this.baseUrl}${this.applicationPath}`;
    const params = new HttpParams()
      .set('id', id)
      .set('fileName', fileDownloadName);

    return from(this.keycloakService.getToken()).pipe(
      switchMap(token => {
        return this.http.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: params,
          responseType: 'blob',
        });
      })
    );
  }

  downloadFromBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  handleDownload(): void {
    if (!this.selectedAttachment) return;

    if (this.currentBlob) {
      // Download from blob (for remote images)
      this.downloadFromBlob(this.currentBlob, this.selectedAttachment.fileName);
    } else if (this.base64Data) {
      // Download from base64 (for PDFs and embedded content)
      this.downloadFile(this.base64Data, this.selectedAttachment.fileName);
    }
  }

  downloadFile(base64String: string, fileName: string) {
    return fieldCardUtilDownloadFile(base64String, fileName);
  }

  ngOnDestroy(): void {
    this.cleanupObjectUrl();
    this.currentBlob = undefined;
    this.subscriptions.unsubscribe();
  }
}
